import { useMutation, useQuery } from '@tanstack/react-query'
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import Pagination from 'components/elements/pagination'
import { Invoice, InvoiceStatus } from 'interfaces/invoice'
import { request, requestQuery } from 'lib/api/fetch-api'
import Badge, { BadgeColor } from 'lib/components/badge/badge'
import IconButton from 'lib/components/buttons/icon-button'
import { Skeleton } from 'lib/components/skeleton/skeleton'
import { TableBody, TableCell, TableHead, TableHeader, TableRow, Table as UITable } from 'lib/components/table/table'
import { currencyToSymbol } from 'lib/util/billing'
import { displayDate } from 'lib/util/date'
import { DownloadIcon, LoaderCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

const columns: ColumnDef<Invoice>[] = [
  {
    id: 'invoice',
    header: 'Invoice',
    cell: ({ row }) => <div>{row.original.id}</div>,
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) => (
      <div>
        {currencyToSymbol(row.original.currency)}
        {row.original.displayAmount}
      </div>
    ),
  },
  {
    id: 'date',
    header: 'Date',
    cell: ({ row }) => <div>{displayDate(row.original.createdAt)}</div>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => <InvoiceStatusBadge status={row.original.status} />,
  },
  {
    id: 'pdf',
    header: null,
    cell: ({ row }) => {
      return <DownloadInvoiceButton invoiceId={row.original.id} />
    },
  },
]

export default function BillingHistory() {
  const [page, setPage] = useState(1)

  const { data, isFetching, error } = useQuery({
    queryKey: ['invoices', page],
    queryFn: requestQuery({ endpoint: 'getAllInvoices', query: { page } }),
    retry: 3,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const table = useReactTable({
    columns,
    data: data?.data,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
  })

  useEffect(() => {
    if (error) {
      toast.error('There was an error loading invoices')
    }
  }, [error])

  if (isFetching) {
    return <Skeleton className="tw-h-24 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
  }

  return (
    <div className="tw-container tw-w-full tw-grow">
      <div className="tw-block tw-w-full tw-overflow-x-auto tw-rounded-lg tw-bg-white tw-p-4 tw-shadow-md">
        <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
          <h4 className="tw-m-0 tw-p-0 tw-text-lg tw-font-bold">Billing History</h4>
        </div>
        <UITable>
          <TableHeader className="tw-sticky tw--top-4 tw-z-10">
            <TableRow intent="header">
              <TableHead className="tw-w-1/4 tw-px-2">Invoice ID</TableHead>
              <TableHead className="tw-w-1/4 tw-px-2">Amount</TableHead>
              <TableHead className="tw-w-1/4 tw-px-2">Date</TableHead>
              <TableHead className="tw-w-1/4 tw-px-2">Status</TableHead>
              <TableHead className="tw-w-1/4 tw-px-2" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {table?.getRowModel().rows.map((row) => (
              <TableRow key={row.id} intent="body" selected={row.getIsSelected()}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="tw-px-4">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </UITable>
      </div>
      <Pagination
        pageCount={Math.ceil(data?.meta.total / data?.meta.pageSize)}
        onPageChange={({ selected }) => {
          setPage(selected + 1)
          window.scrollTo(0, 0)
        }}
        forcePage={page - 1}
      />
    </div>
  )
}

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const badgeColors = {
    [InvoiceStatus.Paid]: 'success',
    [InvoiceStatus.Draft]: 'neutral',
    [InvoiceStatus.Void]: 'error',
    [InvoiceStatus.Deleted]: 'error',
    [InvoiceStatus.Open]: 'neutral',
    [InvoiceStatus.Uncollectible]: 'error',
  }

  return (
    <Badge color={badgeColors[status] as BadgeColor} style="pill">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  )
}

function DownloadInvoiceButton({ invoiceId }: { invoiceId: number }) {
  const { mutate: downloadInvoice, isPending } = useMutation({
    mutationFn: () => request({ endpoint: 'getInvoicePdfUrl', query: { id: invoiceId } }),
    onSuccess: (data) => {
      window.open(data.url, '_blank')
    },
    onError: (error) => {
      toast.error('There was an error downloading the invoice')
      console.error('There was an error downloading the invoice', error)
    },
  })

  return (
    <IconButton onClick={() => downloadInvoice()} disabled={isPending} color="secondary">
      {isPending ? <LoaderCircleIcon className="tw-h-4 tw-w-4 tw-animate-spin" /> : <DownloadIcon />}
    </IconButton>
  )
}
